// Spec iter122 (Feb 2026) — Homogénéisation des badges de statut AVI.
// Style unique : icône colorée + texte même couleur, sans bordure ni fond,
// font-weight 600, texte 13 px, icône 15 px.
// Spec iter164l (2026-02-25) — Palette contextuelle par sous-type
// (Validation bleu / Accusé orange / Information vert). Les overrides
// sont centralisés dans `/lib/aviSubtypePalette.js` — source unique de
// vérité pour badges, Kanban et tuiles KPI.
import React from "react";
import { Clock, Contrast, CircleCheck, CircleX, Stamp, Eye } from "lucide-react";
import { AVI_STATUS_OVERRIDES } from "@/lib/aviSubtypePalette";

/**
 * Badges de statut AVI — 6 statuts métier (spec iter122, étendu iter145).
 *
 *   • En attente             — Clock,       icône #8a8a8f, texte #c8c8cc
 *   • Partiellement répondu  — Contrast,    #F97316
 *   • Approuvée              — CircleCheck, #22C55E
 *   • Reçu (accusee)         — Stamp,       #F97316   (spec iter145 — libellé « Reçu »)
 *   • Lue (information)      — Eye,         #5aa8e0   (spec iter145)
 *   • Refusée                — CircleX,     #EF4444
 *
 * `annulee` conservé pour compat avec des lignes historiques (rendu gris
 * neutre, même style unifié).
 */
export const AVI_STATUS_BADGE = {
  en_attente: {
    label: "En attente",
    color: "#8a8a8f",
    textColor: "#c8c8cc",
    Icon: Clock,
  },
  partiel_repondu: {
    label: "Partiel",
    color: "#F97316",
    Icon: Contrast,
  },
  approuvee: {
    label: "Approuvée",
    color: "#22C55E",
    Icon: CircleCheck,
  },
  // Spec iter145 (Feb 2026) — libellé UI « Reçu » (backend key inchangé).
  accusee: {
    label: "Reçu",
    color: "#F97316",
    Icon: Stamp,
  },
  // Spec iter164k (2026-02-25) — libellé UI « Lue » passé du bleu
  // #5aa8e0 (obsolète) au vert #2a9a7a — cohérent avec le nouveau
  // logo Information (cercle plein vert). Palette gérée via
  // INFO_STATUS_OVERRIDES quand le sous-type est passé au badge.
  lue: {
    label: "Lue",
    color: "#2a9a7a",
    Icon: Eye,
  },
  refusee: {
    label: "Refusée",
    color: "#EF4444",
    Icon: CircleX,
  },
  annulee: {
    label: "Annulée",
    color: "#8a8a8f",
    textColor: "#c8c8cc",
    Icon: CircleX,
  },
};

export default function AviStatusBadge({ status, size = 15, subtype = null, showDot = false }) {
  const base = AVI_STATUS_BADGE[status];
  if (!base) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  // Spec iter164l — override par sous-type via table centralisée.
  const override = subtype && AVI_STATUS_OVERRIDES[subtype] ? AVI_STATUS_OVERRIDES[subtype][status] : null;
  const meta = override ? { ...base, ...override } : base;
  const Icon = meta.Icon;
  const textColor = meta.textColor || meta.color;
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
      style={{
        color: textColor,
        fontWeight: 600,
        fontSize: 13,
        lineHeight: 1.1,
      }}
      data-testid={`avi-status-${status}`}
    >
      {/* Spec iter164o (2026-02-25) — Pastille 8px optionnelle avant
          l'icône, pour les badges inline du hovercard body et de la
          fiche détail. Complète le pattern « pastille = statut =
          couleur » déjà en place sur KPI/Kanban. */}
      {showDot && (
        <span
          aria-hidden="true"
          className="inline-block rounded-full flex-shrink-0"
          style={{
            width: 8, height: 8,
            background: meta.color,
            boxShadow: `0 0 0 2px ${meta.color}22`,
          }}
        />
      )}
      <Icon size={size} strokeWidth={2.2} style={{ color: meta.color }} />
      <span>{meta.label}</span>
    </span>
  );
}
